export interface FastSimplexNoiseOptions {
    random?: () => number;
    frequency?: number;
    max?: number;
    min?: number;
    octaves?: number;
    amplitude?: number;
    persistence?: number;
}

export default class FastSimplexNoise {
    private readonly grad3: [number, number, number][] = [
        [1, 1, 0],
        [-1, 1, 0],
        [1, -1, 0],
        [-1, -1, 0],
        [1, 0, 1],
        [-1, 0, 1],
        [1, 0, -1],
        [-1, 0, -1],
        [0, 1, 1],
        [0, -1, 1],
        [0, 1, -1],
        [0, -1, -1],
    ];

    private readonly grad4: [number, number, number, number][] = [
        [0, 1, 1, 1],
        [0, 1, 1, -1],
        [0, 1, -1, 1],
        [0, 1, -1, -1],
        [0, -1, 1, 1],
        [0, -1, 1, -1],
        [0, -1, -1, 1],
        [0, -1, -1, -1],
        [1, 0, 1, 1],
        [1, 0, 1, -1],
        [1, 0, -1, 1],
        [1, 0, -1, -1],
        [-1, 0, 1, 1],
        [-1, 0, 1, -1],
        [-1, 0, -1, 1],
        [-1, 0, -1, -1],
        [1, 1, 0, 1],
        [1, 1, 0, -1],
        [1, -1, 0, 1],
        [1, -1, 0, -1],
        [-1, 1, 0, 1],
        [-1, 1, 0, -1],
        [-1, -1, 0, 1],
        [-1, -1, 0, -1],
        [1, 1, 1, 0],
        [1, 1, -1, 0],
        [1, -1, 1, 0],
        [1, -1, -1, 0],
        [-1, 1, 1, 0],
        [-1, 1, -1, 0],
        [-1, -1, 1, 0],
        [-1, -1, -1, 0],
    ];
    private perm: number[] = new Array(512);
    private permMod12: number[] = new Array(512);
    private F2 = 0.5 * (Math.sqrt(3) - 1);
    private G2 = (3 - Math.sqrt(3)) / 6;
    private F3 = 1 / 3;
    private G3 = 1 / 6;
    private F4 = (Math.sqrt(5) - 1) / 4;
    private G4 = (5 - Math.sqrt(5)) / 20;

    private frequency: number;
    private max: number;
    private min: number;
    private octaves: number;
    private amplitude: number;
    private persistence: number;

    constructor(options: FastSimplexNoiseOptions = {}) {
        const random = options.random ?? Math.random;

        const p: number[] = new Array(256);
        for (let i = 0; i < 256; i++) p[i] = Math.floor(random() * 256);

        for (let i = 0; i < 512; i++) {
            this.perm[i] = p[i & 255] ?? 0;
            this.permMod12[i] = (this.perm[i] ?? 0) % 12;
        }

        this.frequency = options.frequency ?? 1.0;
        this.max = options.max ?? 1.0;
        this.min = options.min ?? -1.0;
        this.octaves = options.octaves ?? 1;
        this.amplitude = options.amplitude ?? 1.0;
        this.persistence = options.persistence ?? 0.5;
    }

    private dot(g: number[], x: number, y: number, z?: number, w?: number): number {
        switch (g.length) {
            case 2: return (g[0] ?? 0) * x + (g[1] ?? 0) * y;
            case 3: return (g[0] ?? 0) * x + (g[1] ?? 0) * y + (g[2] ?? 0) * (z ?? 0);
            case 4: return (g[0] ?? 0) * x + (g[1] ?? 0) * y + (g[2] ?? 0) * (z ?? 0) + (g[3] ?? 0) * (w ?? 0);
            default: return 0;
        }
    }

    private scaleNoise(value: number): number {
        return this.min + ((value + 1) / 2) * (this.max - this.min);
    }

    public raw2D(xin: number, yin: number): number {
        const grad3 = this.grad3;
        const permMod12 = this.permMod12;
        const perm = this.perm;

        const s = (xin + yin) * this.F2;
        const i = Math.floor(xin + s);
        const j = Math.floor(yin + s);
        const t = (i + j) * this.G2;
        const X0 = i - t;
        const Y0 = j - t;
        const x0 = xin - X0;
        const y0 = yin - Y0;

        let i1: number, j1: number;
        if (x0 > y0) { i1 = 1; j1 = 0; }
        else { i1 = 0; j1 = 1; }

        const x1 = x0 - i1 + this.G2;
        const y1 = y0 - j1 + this.G2;
        const x2 = x0 - 1.0 + 2.0 * this.G2;
        const y2 = y0 - 1.0 + 2.0 * this.G2;

        const ii = i & 255;
        const jj = j & 255;
        const gi0 = permMod12[ii + (perm[jj] ?? 0)];
        const gi1 = permMod12[ii + i1 + (perm[jj + j1] ?? 0)];
        const gi2 = permMod12[ii + 1 + (perm[jj + 1] ?? 0)];

        let n0 = 0, n1 = 0, n2 = 0;

        let t0 = 0.5 - x0 * x0 - y0 * y0;
        if (t0 >= 0) {
            t0 *= t0;
            n0 = t0 * t0 * this.dot((grad3[gi0 ?? 0] ?? []), x0, y0);
        }

        let t1 = 0.5 - x1 * x1 - y1 * y1;
        if (t1 >= 0) {
            t1 *= t1;
            n1 = t1 * t1 * this.dot(grad3[gi1 ?? 0] ?? [], x1, y1);
        }

        let t2 = 0.5 - x2 * x2 - y2 * y2;
        if (t2 >= 0) {
            t2 *= t2;
            n2 = t2 * t2 * this.dot((grad3[gi2 ?? 0] ?? []), x2, y2);
        }

        return 70.0 * (n0 + n1 + n2);
    }

    public getRaw4DNoise(x: number, y: number, z: number, w: number): number {
        const F4 = this.F4;
        const G4 = this.G4;

        const s = (x + y + z + w) * F4;
        const i = Math.floor(x + s);
        const j = Math.floor(y + s);
        const k = Math.floor(z + s);
        const l = Math.floor(w + s);
        const t = (i + j + k + l) * G4;

        const X0 = i - t;
        const Y0 = j - t;
        const Z0 = k - t;
        const W0 = l - t;

        const x0 = x - X0;
        const y0 = y - Y0;
        const z0 = z - Z0;
        const w0 = w - W0;

        let rankx = 0, ranky = 0, rankz = 0, rankw = 0;
        if (x0 > y0) rankx++; else ranky++;
        if (x0 > z0) rankx++; else rankz++;
        if (x0 > w0) rankx++; else rankw++;
        if (y0 > z0) ranky++; else rankz++;
        if (y0 > w0) ranky++; else rankw++;
        if (z0 > w0) rankz++; else rankw++;

        const i1 = rankx >= 3 ? 1 : 0;
        const j1 = ranky >= 3 ? 1 : 0;
        const k1 = rankz >= 3 ? 1 : 0;
        const l1 = rankw >= 3 ? 1 : 0;

        const i2 = rankx >= 2 ? 1 : 0;
        const j2 = ranky >= 2 ? 1 : 0;
        const k2 = rankz >= 2 ? 1 : 0;
        const l2 = rankw >= 2 ? 1 : 0;

        const i3 = rankx >= 1 ? 1 : 0;
        const j3 = ranky >= 1 ? 1 : 0;
        const k3 = rankz >= 1 ? 1 : 0;
        const l3 = rankw >= 1 ? 1 : 0;

        const x1 = x0 - i1 + G4;
        const y1 = y0 - j1 + G4;
        const z1 = z0 - k1 + G4;
        const w1 = w0 - l1 + G4;

        const x2 = x0 - i2 + 2.0 * G4;
        const y2 = y0 - j2 + 2.0 * G4;
        const z2 = z0 - k2 + 2.0 * G4;
        const w2 = w0 - l2 + 2.0 * G4;

        const x3 = x0 - i3 + 3.0 * G4;
        const y3 = y0 - j3 + 3.0 * G4;
        const z3 = z0 - k3 + 3.0 * G4;
        const w3 = w0 - l3 + 3.0 * G4;

        const x4 = x0 - 1.0 + 4.0 * G4;
        const y4 = y0 - 1.0 + 4.0 * G4;
        const z4 = z0 - 1.0 + 4.0 * G4;
        const w4 = w0 - 1.0 + 4.0 * G4;

        const ii = i & 255;
        const jj = j & 255;
        const kk = k & 255;
        const ll = l & 255;

        const perm = this.perm;
        const grad4 = this.grad4;

        let n0 = 0, n1 = 0, n2 = 0, n3 = 0, n4 = 0;

        const getGrad = (i: number, j: number, k: number, l: number) => {
            // Ensure all array accesses are safe and defined
            const llIndex = ll + l;
            const permLL = perm[llIndex];
            if (permLL === undefined) return [0, 0, 0, 0];

            const kkIndex = kk + k + permLL;
            const permKK = perm[kkIndex];
            if (permKK === undefined) return [0, 0, 0, 0];

            const jjIndex = jj + j + permKK;
            const permJJ = perm[jjIndex];
            if (permJJ === undefined) return [0, 0, 0, 0];

            const iiIndex = ii + i + permJJ;
            const permII = perm[iiIndex];
            if (permII === undefined) return [0, 0, 0, 0];

            const index = permII % 32;
            return grad4[index] ?? [0, 0, 0, 0];
        };

        const contrib = (x: number, y: number, z: number, w: number, g: [number, number, number, number]) => {
            const t = 0.6 - x * x - y * y - z * z - w * w;
            if (t < 0) return 0.0;
            const t2 = t * t;
            return t2 * t2 * this.dot(g, x, y, z, w);
        };

        n0 = contrib(x0, y0, z0, w0, getGrad(0, 0, 0, 0) as [number, number, number, number]);
        n1 = contrib(x1, y1, z1, w1, getGrad(i1, j1, k1, l1) as [number, number, number, number]);
        n2 = contrib(x2, y2, z2, w2, getGrad(i2, j2, k2, l2) as [number, number, number, number]);
        n3 = contrib(x3, y3, z3, w3, getGrad(i3, j3, k3, l3) as [number, number, number, number]);
        n4 = contrib(x4, y4, z4, w4, getGrad(1, 1, 1, 1) as [number, number, number, number]);

        return 27.0 * (n0 + n1 + n2 + n3 + n4);
    }

    public scaled2D(x: number, y: number): number {
        return this.scaleNoise(this.raw2D(x * this.frequency, y * this.frequency));
    }

}